import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { SparklesIcon, TrendingUpIcon, ChromeIcon, AwardIcon, DownloadIcon, PlayIcon, BookOpenIcon, CheckCircleIcon } from 'lucide-react';
export function FreeToolsPage() {
  const {
    tool
  } = useParams<{
    tool: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'forecasting' | 'prompts' | 'extension' | 'training'>('forecasting');
  useEffect(() => {
    if (tool) {
      setActiveTab(tool as any);
    }
  }, [tool]);
  const handleTabChange = (tab: string) => {
    setActiveTab(tab as any);
    navigate(`/free-tools/${tab}`);
  };
  const prompts = [{
    id: 1,
    title: 'Account Research Summary',
    category: 'Research',
    description: 'Generate comprehensive account summaries with key insights and buying signals',
    uses: 1234
  }, {
    id: 2,
    title: 'Personalized Email Opener',
    category: 'Outreach',
    description: 'Create engaging email openers based on account context and recent activity',
    uses: 2456
  }, {
    id: 3,
    title: 'Objection Handler',
    category: 'Sales',
    description: 'Get AI-powered responses to common objections with personalization',
    uses: 987
  }, {
    id: 4,
    title: 'Meeting Prep Brief',
    category: 'Preparation',
    description: 'Prepare for meetings with AI-generated briefings on key stakeholders',
    uses: 1567
  }];
  const trainingModules = [{
    id: 1,
    title: 'GTM Studio Fundamentals',
    lessons: 8,
    duration: '2 hours',
    completed: true
  }, {
    id: 2,
    title: 'Advanced Segmentation',
    lessons: 6,
    duration: '1.5 hours',
    completed: true
  }, {
    id: 3,
    title: 'AI Agents & Automation',
    lessons: 10,
    duration: '3 hours',
    completed: false
  }, {
    id: 4,
    title: 'Orchestration Mastery',
    lessons: 12,
    duration: '4 hours',
    completed: false
  }];
  return <div className="w-full h-screen flex flex-col bg-gray-50" data-id="element-5007">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 lg:py-4" data-id="element-5008">
        <div className="flex items-center justify-between" data-id="element-5009">
          <div data-id="element-5010">
            <div className="flex items-center gap-2" data-id="element-5011">
              <SparklesIcon className="w-5 h-5 lg:w-6 lg:h-6 text-red-600" data-id="element-5012" />
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900" data-id="element-5013">
                Free AI Tools & Browser Extension
              </h1>
            </div>
            <p className="text-xs lg:text-sm text-gray-600 mt-1" data-id="element-5014">
              Powerful GTM tools and resources, completely free
            </p>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 lg:px-6 overflow-x-auto" data-id="element-5015">
        <div className="flex gap-4 lg:gap-6 min-w-max" data-id="element-5016">
          {[{
          id: 'forecasting',
          label: 'Forecasting Tool',
          icon: TrendingUpIcon
        }, {
          id: 'prompts',
          label: 'Prompt Library',
          icon: SparklesIcon
        }, {
          id: 'extension',
          label: 'Browser Extension',
          icon: ChromeIcon
        }, {
          id: 'training',
          label: 'Training Portal',
          icon: AwardIcon
        }].map(tabItem => {
          const Icon = tabItem.icon;
          return <button key={tabItem.id} onClick={() => handleTabChange(tabItem.id)} className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tabItem.id ? 'border-red-600 text-red-600' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-5017">
                <Icon className="w-4 h-4" data-id="element-5018" />
                <span className="hidden sm:inline" data-id="element-5019">{tabItem.label}</span>
              </button>;
        })}
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 lg:p-6" data-id="element-5020">
        {activeTab === 'forecasting' && <div className="max-w-5xl mx-auto" data-id="element-5021">
            <div className="bg-white rounded-lg border border-gray-200 p-6 lg:p-8 mb-6" data-id="element-5022">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4" data-id="element-5023">
                GTM Forecasting Calculator
              </h2>
              <p className="text-sm lg:text-base text-gray-600 mb-6" data-id="element-5024">
                Model your GTM strategy and forecast outcomes based on key
                metrics
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5025">
                <div data-id="element-5026">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5027">
                    Target Accounts
                  </label>
                  <input type="number" placeholder="1000" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5028" />
                </div>
                <div data-id="element-5029">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5030">
                    Conversion Rate (%)
                  </label>
                  <input type="number" placeholder="15" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5031" />
                </div>
                <div data-id="element-5032">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5033">
                    Average Deal Size ($)
                  </label>
                  <input type="number" placeholder="50000" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5034" />
                </div>
                <div data-id="element-5035">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5036">
                    Sales Cycle (days)
                  </label>
                  <input type="number" placeholder="90" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-5037" />
                </div>
              </div>
              <button className="w-full mt-6 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium" data-id="element-5038">
                Calculate Forecast
              </button>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg border border-green-200 p-6 lg:p-8" data-id="element-5039">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-5040">
                Projected Results
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5041">
                <div className="bg-white rounded-lg p-4" data-id="element-5042">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-5043">
                    Expected Pipeline
                  </p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-5044">$7.5M</p>
                </div>
                <div className="bg-white rounded-lg p-4" data-id="element-5045">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-5046">Closed Deals</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-5047">150</p>
                </div>
                <div className="bg-white rounded-lg p-4" data-id="element-5048">
                  <p className="text-sm text-gray-600 mb-1" data-id="element-5049">Revenue</p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-5050">$7.5M</p>
                </div>
              </div>
            </div>
          </div>}
        {activeTab === 'prompts' && <div className="max-w-7xl mx-auto" data-id="element-5051">
            <div className="mb-6" data-id="element-5052">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2" data-id="element-5053">
                AI Prompt Library
              </h2>
              <p className="text-sm lg:text-base text-gray-600" data-id="element-5054">
                Pre-built prompts to accelerate your GTM workflows
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6" data-id="element-5055">
              {prompts.map(prompt => <div key={prompt.id} className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6 hover:shadow-lg transition-shadow" data-id="element-5056">
                  <div className="flex items-start justify-between mb-3" data-id="element-5057">
                    <div className="flex-1" data-id="element-5058">
                      <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-1" data-id="element-5059">
                        {prompt.title}
                      </h3>
                      <span className="inline-block px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full" data-id="element-5060">
                        {prompt.category}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-4" data-id="element-5061">
                    {prompt.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-5062">
                    <span className="text-xs text-gray-500" data-id="element-5063">
                      {prompt.uses.toLocaleString()} uses
                    </span>
                    <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm" data-id="element-5064">
                      <SparklesIcon className="w-4 h-4" data-id="element-5065" />
                      Use Prompt
                    </button>
                  </div>
                </div>)}
            </div>
          </div>}
        {activeTab === 'extension' && <div className="max-w-5xl mx-auto" data-id="element-5066">
            <div className="bg-white rounded-lg border border-gray-200 p-6 lg:p-8 mb-6" data-id="element-5067">
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 mb-8" data-id="element-5068">
                <div className="w-20 h-20 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0" data-id="element-5069">
                  <ChromeIcon className="w-10 h-10 text-red-600" data-id="element-5070" />
                </div>
                <div className="flex-1" data-id="element-5071">
                  <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2" data-id="element-5072">
                    ZoomInfo Chrome Extension
                  </h2>
                  <p className="text-sm lg:text-base text-gray-600" data-id="element-5073">
                    Access ZoomInfo data and AI insights directly in your
                    browser
                  </p>
                </div>
                <button className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium whitespace-nowrap" data-id="element-5074">
                  <DownloadIcon className="w-5 h-5" data-id="element-5075" />
                  Install Extension
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5076">
                <div className="p-4 bg-gray-50 rounded-lg" data-id="element-5077">
                  <CheckCircleIcon className="w-8 h-8 text-green-600 mb-3" data-id="element-5078" />
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-5079">
                    LinkedIn Integration
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-5080">
                    View contact info and insights on LinkedIn profiles
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg" data-id="element-5081">
                  <CheckCircleIcon className="w-8 h-8 text-green-600 mb-3" data-id="element-5082" />
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-5083">CRM Sync</h3>
                  <p className="text-sm text-gray-600" data-id="element-5084">
                    Instantly add contacts to your CRM with one click
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg" data-id="element-5085">
                  <CheckCircleIcon className="w-8 h-8 text-green-600 mb-3" data-id="element-5086" />
                  <h3 className="font-semibold text-gray-900 mb-2" data-id="element-5087">
                    AI Insights
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-5088">
                    Get AI-powered account summaries and recommendations
                  </p>
                </div>
              </div>
            </div>
          </div>}
        {activeTab === 'training' && <div className="max-w-5xl mx-auto" data-id="element-5089">
            <div className="mb-6" data-id="element-5090">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2" data-id="element-5091">
                Training Portal
              </h2>
              <p className="text-sm lg:text-base text-gray-600" data-id="element-5092">
                Master the ZoomInfo GTM Platform with our comprehensive training
              </p>
            </div>
            <div className="space-y-4" data-id="element-5093">
              {trainingModules.map(module => <div key={module.id} className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6 hover:shadow-md transition-shadow" data-id="element-5094">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4" data-id="element-5095">
                    <div className="flex-1" data-id="element-5096">
                      <div className="flex items-center gap-3 mb-2" data-id="element-5097">
                        <h3 className="text-base lg:text-lg font-semibold text-gray-900" data-id="element-5098">
                          {module.title}
                        </h3>
                        {module.completed && <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-5099" />}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 lg:gap-4 text-sm text-gray-600" data-id="element-5100">
                        <span className="flex items-center gap-1" data-id="element-5101">
                          <BookOpenIcon className="w-4 h-4" data-id="element-5102" />
                          {module.lessons} lessons
                        </span>
                        <span className="flex items-center gap-1" data-id="element-5103">
                          <PlayIcon className="w-4 h-4" data-id="element-5104" />
                          {module.duration}
                        </span>
                      </div>
                    </div>
                    <button className={`px-6 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${module.completed ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-red-600 text-white hover:bg-red-700'}`} data-id="element-5105">
                      {module.completed ? 'Review' : 'Start Module'}
                    </button>
                  </div>
                </div>)}
            </div>
          </div>}
      </div>
    </div>;
}